import { create } from 'zustand';

/**
 * Re-auth coordinator.
 *
 * Centralizes the "session expired" recovery flow so any API call that
 * receives a 401 can pause, ask the user to sign in again, and resume
 * exactly where it left off.
 *
 * Two cooperating pieces:
 *
 * 1. `requestReauth()` returns a promise that resolves once the user has
 *    successfully re-authenticated through `<ReauthModal>`. `apiFetch`
 *    awaits this promise on 401 and then retries the original request
 *    (Requirement 20.4: "without losing in-progress form data" — see #2).
 *
 * 2. `formSnapshots` is a string-keyed slice of arbitrary JSON-shaped form
 *    data. Long-lived forms (pack editor, asset uploader) write their
 *    current draft into this slice on every change. When the re-auth modal
 *    pops, the form behind it remains mounted because the modal is a
 *    Radix overlay — but if the user closes the tab and re-opens, or if a
 *    future flow forces a redirect, the form can rehydrate from the
 *    snapshot. This is the "preserves in-flight form data" half of 20.4
 *    documented in `src/components/ReauthModal.tsx`.
 *
 * Validates Requirement 20.4.
 */

type Pending = {
  resolve(): void;
  reject(error: unknown): void;
};

interface ReauthStore {
  /** Whether the modal is currently visible. */
  isOpen: boolean;
  /**
   * The in-flight 401 awaiters. Multiple concurrent requests can be paused
   * and woken up by a single re-auth, so we keep an array.
   */
  waiters: Pending[];
  /** Per-form snapshot store keyed by a stable form id. */
  formSnapshots: Record<string, unknown>;

  /**
   * Open the modal (idempotent) and return a promise that resolves when
   * the user successfully re-authenticates. If the user dismisses the
   * modal without signing in, all pending waiters reject and callers
   * propagate the original 401 to their normal error handling.
   */
  requestReauth(): Promise<void>;
  /** Called by `<ReauthModal>` after a successful sign-in. */
  resolveReauth(): void;
  /** Called by `<ReauthModal>` when the user cancels. */
  rejectReauth(error: unknown): void;

  setFormSnapshot(formId: string, data: unknown): void;
  getFormSnapshot<T = unknown>(formId: string): T | undefined;
  clearFormSnapshot(formId: string): void;
}

export const useReauthStore = create<ReauthStore>((set, get) => ({
  isOpen: false,
  waiters: [],
  formSnapshots: {},

  requestReauth: () =>
    new Promise<void>((resolve, reject) => {
      set((state) => ({
        isOpen: true,
        waiters: [...state.waiters, { resolve, reject }],
      }));
    }),

  resolveReauth: () => {
    const waiters = get().waiters;
    set({ isOpen: false, waiters: [] });
    for (const w of waiters) {
      w.resolve();
    }
  },

  rejectReauth: (error) => {
    const waiters = get().waiters;
    set({ isOpen: false, waiters: [] });
    for (const w of waiters) {
      w.reject(error);
    }
  },

  setFormSnapshot: (formId, data) =>
    set((state) => ({
      formSnapshots: { ...state.formSnapshots, [formId]: data },
    })),
  getFormSnapshot: <T,>(formId: string): T | undefined =>
    get().formSnapshots[formId] as T | undefined,
  clearFormSnapshot: (formId) =>
    set((state) => {
      const next = { ...state.formSnapshots };
      delete next[formId];
      return { formSnapshots: next };
    }),
}));
